/**
 * OLIMPO STORY VIDEO PLAYER
 * ─────────────────────────────────────────────────────────────────────────
 * Player vertical full-screen estilo Instagram Reels / TikTok pra vídeos do
 * produto. Renderizado pelo snippet olimpo-story-video-player.liquid e
 * acionado por thumbs circulares (olimpo-story-video.liquid).
 *
 * FLUXO:
 *   1. Thumb [data-story-open][data-story-index="N"] recebe click
 *   2. JS lê [data-story-videos] (JSON) + [data-story-product] (JSON)
 *   3. Abre modal [data-story-player], troca src do video, toca autoplay muted
 *   4. User navega por swipe (mobile) / arrows (desktop) / click nos nav btns
 *   5. X ou ESC fecha, restaura foco + scroll
 *
 * BEST PRACTICES:
 *   • IIFE + guard global pra evitar dupla execução
 *   • Focus trap durante modal aberto
 *   • Body lock via classe no <html>
 *   • touchstart/touchend pra swipe mobile (threshold 40px)
 *   • Respeita prefers-reduced-motion (skip transitions, funcionalidade ok)
 *   • Cleanup listeners no close
 */

(function () {
  'use strict';

  if (window.__olimpoStoryVideoBooted) return;
  window.__olimpoStoryVideoBooted = true;

  /* ── Estado global do player ──────────────────────────────────────── */
  var state = {
    player: null,
    videoEl: null,
    progressWrap: null,
    progressBars: [],
    videos: [],
    product: null,
    currentIndex: 0,
    lastFocused: null,
    rafId: null,
    touchStartY: 0,
    touchStartX: 0,
    scrollCooldown: 0
  };

  /* ── Init: bind abrir modal em cada thumb ─────────────────────────── */
  function init() {
    state.player = document.querySelector('[data-story-player]');
    if (!state.player) return;

    state.videoEl = state.player.querySelector('[data-story-video]');
    state.progressWrap = state.player.querySelector('[data-story-progress]');

    /* Bind open em todos os thumbs da página (pode ter múltiplas galerias
       em produtos com related cards, mas na PDP normalmente só uma) */
    document.querySelectorAll('[data-story-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var rootEl = btn.closest('[data-story-video-root]');
        if (!rootEl) return;
        loadFromRoot(rootEl);
        var idx = parseInt(btn.dataset.storyIndex, 10) || 0;
        openPlayer(idx, btn);
      });
    });

    /* Controles do player */
    var closeBtn = state.player.querySelector('[data-story-close]');
    var prevBtn = state.player.querySelector('[data-story-prev]');
    var nextBtn = state.player.querySelector('[data-story-next]');
    var soundBtn = state.player.querySelector('[data-story-sound]');
    var stageEl = state.player.querySelector('[data-story-stage]');
    var ctaBtn = state.player.querySelector('[data-story-cta]');

    if (closeBtn) closeBtn.addEventListener('click', closePlayer);
    if (prevBtn)  prevBtn.addEventListener('click', function () { go(-1); });
    if (nextBtn)  nextBtn.addEventListener('click', function () { go(1); });
    if (soundBtn) soundBtn.addEventListener('click', toggleSound);
    if (stageEl)  stageEl.addEventListener('click', togglePlayPause);
    if (ctaBtn)   ctaBtn.addEventListener('click', handleCtaClick);

    /* Swipe mobile + teclado desktop — bindados apenas quando modal aberto */
  }

  /* ── Carrega dados do <script type="application/json"> ─────────────── */
  function loadFromRoot(rootEl) {
    try {
      var videosEl = rootEl.querySelector('[data-story-videos]');
      var productEl = rootEl.querySelector('[data-story-product]');
      if (videosEl) state.videos = JSON.parse(videosEl.textContent);
      if (productEl) state.product = JSON.parse(productEl.textContent);
    } catch (e) {
      /* JSON mal formado (edge case) — fail silencioso, modal não abre */
      state.videos = [];
      state.product = null;
    }
  }

  /* ── Monta as progress bars (uma por vídeo) ──────────────────────── */
  function buildProgressBars() {
    if (!state.progressWrap) return;
    state.progressWrap.innerHTML = '';
    state.progressBars = [];
    for (var i = 0; i < state.videos.length; i++) {
      var bar = document.createElement('div');
      bar.className = 'olimpo-story-player__progress-bar';
      bar.style.setProperty('--progress', '0%');
      state.progressWrap.appendChild(bar);
      state.progressBars.push(bar);
    }
  }

  /* ── Popula footer com dados do produto ──────────────────────────── */
  function populateFooter() {
    if (!state.product) return;
    var img = state.player.querySelector('[data-story-product-img]');
    var title = state.player.querySelector('[data-story-product-title]');
    var price = state.player.querySelector('[data-story-product-price]');
    if (img) img.src = state.product.image || '';
    if (title) title.textContent = state.product.title || '';
    if (price) price.textContent = state.product.price || '';
  }

  /* ── Abre o modal e reproduz o índice N ───────────────────────────── */
  function openPlayer(index, fromEl) {
    if (!state.videos.length) return;
    state.currentIndex = Math.max(0, Math.min(index, state.videos.length - 1));
    state.lastFocused = fromEl || document.activeElement;

    buildProgressBars();
    populateFooter();

    state.player.hidden = false;
    state.player.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('olimpo-story-locked');

    /* Bind keyboard + touch handlers agora (removidos no close) */
    document.addEventListener('keydown', onKeydown);
    state.player.addEventListener('touchstart', onTouchStart, { passive: true });
    state.player.addEventListener('touchend', onTouchEnd, { passive: true });
    state.player.addEventListener('wheel', onWheel, { passive: true });

    playIndex(state.currentIndex);

    /* Foco inicial no botão de fechar */
    var closeBtn = state.player.querySelector('[data-story-close]');
    if (closeBtn) closeBtn.focus();
  }

  /* ── Fecha modal, restaura scroll + foco ──────────────────────────── */
  function closePlayer() {
    if (state.player.hidden) return;
    stopProgressLoop();
    if (state.videoEl) {
      try { state.videoEl.pause(); } catch (e) {}
      state.videoEl.removeAttribute('src');
      state.videoEl.load();
    }
    state.player.setAttribute('aria-hidden', 'true');
    state.player.hidden = true;
    document.documentElement.classList.remove('olimpo-story-locked');

    document.removeEventListener('keydown', onKeydown);
    state.player.removeEventListener('touchstart', onTouchStart);
    state.player.removeEventListener('touchend', onTouchEnd);
    state.player.removeEventListener('wheel', onWheel);

    if (state.lastFocused && state.lastFocused.focus) {
      state.lastFocused.focus();
    }
  }

  /* ── Reproduz vídeo no índice N ───────────────────────────────────── */
  function playIndex(i) {
    if (!state.videoEl || !state.videos[i]) return;
    var data = state.videos[i];
    state.videoEl.src = data.url;
    if (data.poster) state.videoEl.setAttribute('poster', data.poster);
    state.videoEl.load();
    var playPromise = state.videoEl.play();
    if (playPromise && playPromise.catch) {
      /* Browser bloqueou autoplay (raro com muted) — ignora silenciosamente */
      playPromise.catch(function () {});
    }
    updateProgressHighlight(i);
    startProgressLoop();
    updateNavButtons();
  }

  /* ── Navega entre vídeos ──────────────────────────────────────────── */
  function go(direction) {
    var next = state.currentIndex + direction;
    if (next < 0 || next >= state.videos.length) return;
    state.currentIndex = next;
    playIndex(next);
  }

  /* ── Progress bar update via requestAnimationFrame ─────────────────── */
  function startProgressLoop() {
    stopProgressLoop();
    var loop = function () {
      if (!state.videoEl) return;
      var dur = state.videoEl.duration || 0;
      var cur = state.videoEl.currentTime || 0;
      if (dur > 0) {
        var pct = Math.min(100, (cur / dur) * 100);
        var bar = state.progressBars[state.currentIndex];
        if (bar) bar.style.setProperty('--progress', pct + '%');
        /* Avança pro próximo automaticamente quando chega no fim */
        if (pct >= 99.5 && state.currentIndex < state.videos.length - 1) {
          go(1);
          return;
        }
      }
      state.rafId = requestAnimationFrame(loop);
    };
    state.rafId = requestAnimationFrame(loop);
  }

  function stopProgressLoop() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
  }

  /* ── Highlight: barras anteriores 100%, atual animando, futuras 0% ── */
  function updateProgressHighlight(activeIndex) {
    state.progressBars.forEach(function (bar, i) {
      if (i < activeIndex) bar.style.setProperty('--progress', '100%');
      else if (i > activeIndex) bar.style.setProperty('--progress', '0%');
      /* bar === active é atualizada pelo loop */
    });
  }

  /* ── Disable prev/next nos extremos ───────────────────────────────── */
  function updateNavButtons() {
    var prev = state.player.querySelector('[data-story-prev]');
    var next = state.player.querySelector('[data-story-next]');
    if (prev) prev.disabled = state.currentIndex === 0;
    if (next) next.disabled = state.currentIndex >= state.videos.length - 1;
  }

  /* ── Som on/off ───────────────────────────────────────────────────── */
  function toggleSound() {
    if (!state.videoEl) return;
    state.videoEl.muted = !state.videoEl.muted;
    var btn = state.player.querySelector('[data-story-sound]');
    if (btn) {
      btn.setAttribute('aria-pressed', state.videoEl.muted ? 'false' : 'true');
      btn.setAttribute('aria-label', state.videoEl.muted ? 'Ativar som' : 'Silenciar');
    }
  }

  /* ── Play/pause ao tap no stage ───────────────────────────────────── */
  function togglePlayPause() {
    if (!state.videoEl) return;
    if (state.videoEl.paused) state.videoEl.play().catch(function () {});
    else state.videoEl.pause();
  }

  /* ── CTA "Shop": fecha modal e leva pros buy_buttons da PDP ───────── */
  function handleCtaClick() {
    var productInfoBtn = document.querySelector('product-form button[name="add"], .product-form__buttons');
    closePlayer();
    if (productInfoBtn) {
      setTimeout(function () {
        productInfoBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }

  /* ── Keyboard handlers (só quando modal aberto) ───────────────────── */
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closePlayer();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    } else if (e.key === ' ') {
      e.preventDefault();
      togglePlayPause();
    } else if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      toggleSound();
    } else if (e.key === 'Tab') {
      /* Focus trap simples — mantém foco dentro do modal */
      var focusable = state.player.querySelectorAll('button:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Touch: swipe up/down muda vídeo ──────────────────────────────── */
  function onTouchStart(e) {
    if (!e.touches || !e.touches.length) return;
    state.touchStartY = e.touches[0].clientY;
    state.touchStartX = e.touches[0].clientX;
  }

  function onTouchEnd(e) {
    if (!e.changedTouches || !e.changedTouches.length) return;
    var dy = e.changedTouches[0].clientY - state.touchStartY;
    var dx = e.changedTouches[0].clientX - state.touchStartX;
    /* Swipe vertical mais forte que horizontal + threshold 40px */
    if (Math.abs(dy) > 40 && Math.abs(dy) > Math.abs(dx)) {
      if (dy < 0) go(1);   /* swipe up → próximo */
      else       go(-1);   /* swipe down → anterior */
    }
  }

  /* ── Wheel (desktop): scroll direcional com cooldown ──────────────── */
  function onWheel(e) {
    var now = Date.now();
    if (now < state.scrollCooldown) return;
    if (Math.abs(e.deltaY) < 20) return;
    state.scrollCooldown = now + 600; /* 600ms cooldown evita voos */
    if (e.deltaY > 0) go(1);
    else              go(-1);
  }

  /* ── Boot ─────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
